import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { event } from '@uhg-abyss/web/tools/event';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useContext } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Constants } from '../../common/Constants';
import { phoneOnly } from '../../common/ConstantsStyles';
import { PortalContext } from '../../context/PortalContext';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useGuestStore } from '../../store/useGuestStore';
import { GuestState } from '../../store/useGuestStore/guestStore';
import { getIsIFPEarlyPay } from '../../utils/guest.utils';
import { getLoggedInMember } from '../../utils/user.utils';
import { StoreKeys } from '../useStore/state';
import { useStore } from '../useStore/useStore';

const getCoverageTypeCodes = (loggedInMember: any): string => {
  const coverageTypeCodes =
    loggedInMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.map(
      (coverage: any) => coverage?.typeCode?.coverageTypeCode
    );
  return coverageTypeCodes?.join('|') || '';
};

const getMedicalCoverage = (loggedInMember: any): any | undefined =>
  loggedInMember?.eligibility?.[0]?.memberHealthCoverage?.coverageType?.find(
    (coverage: any) => coverage?.typeCode?.coverageTypeCode === 'M'
  );

const getSitusStateForMedicalCoverage = (
  loggedInMember: any
): string | undefined => {
  const medicalCoverage = getMedicalCoverage(loggedInMember);
  return medicalCoverage?.situsState;
};

const getMedicalCoverageStartDate = (
  loggedInMember: any
): string | undefined => {
  const medicalCoverage = getMedicalCoverage(loggedInMember);
  return medicalCoverage?.coverageEffDateRange?.startDate;
};

export const useAdobePageTrackEvent = (input) => {
  const { accountId } = getPlanSelectorVariables();
  const { bigN } = useStore(StoreKeys.PLAN_SELECTOR);
  const mobileScreen = useMediaQuery(phoneOnly);
  const loggedInMember = getLoggedInMember();
  const loggedInMemberRelationshipTypeCode =
    loggedInMember?.demographics?.relationshipTypeCode?.code.toLowerCase() ||
    '';
  const coverageTypeCodesString = getCoverageTypeCodes(loggedInMember) || '';
  const situsState = getSitusStateForMedicalCoverage(loggedInMember);
  const medicalCoverageStartDate = getMedicalCoverageStartDate(loggedInMember);
  const isSuperUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.SUPER_USER
  );
  let language = '';
  const locale = getLanguage()?.code || 'en';
  if (locale === 'es') {
    language = 'spanish';
  } else language = 'english';
  const { portalData } = useContext(PortalContext);

  const { guestData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestData: state.guestData,
    }))
  );

  const userTier = portalData?.portalConfig?.userTier;
  const portalIdentifier = portalData?.portalIdentifier;
  const portalName = portalData?.portalName;
  const isSsoPortal = portalData?.portalConfig?.isSsoPortal;
  const { impressionBlock, providerBlock, searchBlock, isGuest, guestLob } =
    input;

  let lob = loggedInMember?.lineOfBusiness?.toLowerCase();
  let referringSite = '';
  let hsid = accountId;
  let sitesection1 = 'secure';
  const portalEligibilityDataPolicyId =
    portalData?.portalEligibilityData?.policyId || '';

  if (isSsoPortal) {
    referringSite = `${portalName}_${portalIdentifier}`;
    hsid = '';
  } else if (getIsIFPEarlyPay(guestData)) {
    lob = 'guest';
    referringSite = 'ifpEarlyPay';
    sitesection1 = 'public';
  } else if (isGuest) {
    lob = guestLob;
    sitesection1 = 'public';
  }

  const ssoUserDetails = {
    planID: portalData?.portalEligibilityData?.delsysCode,
    employerName: portalName,
    planCoverageType: coverageTypeCodesString?.toLowerCase(),
    membershipCategory: loggedInMember?.membershipCategory || '',
    planState: situsState || '',
    planStartDate: medicalCoverageStartDate || '',
    planName: portalData?.planName || '',
    planType: portalData?.portalIdentifier?.toLowerCase() || '',
    planBrand: loggedInMember?.membershipCategory || '',
    payerGroup: loggedInMember?.lineOfBusiness?.toLowerCase() || '',
  };

  const adobePageTrackEvent = () => {
    event('PAGE_TRACK_EVENT', {
      page: {
        experienceType: mobileScreen ? 'mobile' : 'desktop',
        is404: input?.is404 ?? false,
        lang: language,
        name: input?.pageName?.toLowerCase(),
        sitesection1,
        sitesection2: 'find care',
        sitesection3: input?.sitesectionLevel1?.toLowerCase() ?? '',
        sitesection4: input?.sitesectionLevel2?.toLowerCase() ?? '',
        test: '',
        referringSite,
        group: 'search',
      },
      user: {
        loginStatus: isGuest ? 'not logged in' : 'logged in',
        userType: `member>${lob}`,
        groupId: bigN?.GroupNumber || portalEligibilityDataPolicyId,
        carrierId: '',
        hsid,
        rallyId: loggedInMember?.rallyId,
        patientId: '',
        payerGroup: lob,
        planStatus: isGuest
          ? ''
          : loggedInMember?.isPreEffective
          ? 'pre-effective'
          : 'active',
        accountId,
        userCategory: isSuperUser
          ? 'superuser'
          : loggedInMemberRelationshipTypeCode,
        customAttributes: {
          tier: `tier ${userTier}`,
        },
        ...(isSsoPortal && ssoUserDetails),
      },
      ...(impressionBlock && { impression: impressionBlock }),
      ...(providerBlock && { provider: providerBlock }),
      ...(searchBlock && { search: searchBlock }),
    });
  };

  return { adobePageTrackEvent };
};
